import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Bot } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { agentService } from '@/services/agent'
import type { Agent } from '@/schemas/agent'

interface AgentSelectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  onSelect: (agentId: string, agentName: string) => void
}

export function AgentSelectDialog({
  open,
  onOpenChange,
  projectId,
  onSelect,
}: AgentSelectDialogProps) {
  const { t } = useTranslation('chat')
  const [agents, setAgents] = useState<Agent[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!open || !projectId) return

    const fetchAgents = async () => {
      setIsLoading(true)
      try {
        const response = await agentService.listAgents(projectId, {
          page: 1,
          page_size: 100,
          is_active: true,
        })
        setAgents(response.agents)
      } catch (error) {
        console.error('Failed to fetch agents:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchAgents()
  }, [open, projectId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('selectAgent.title')}</DialogTitle>
          <DialogDescription>{t('selectAgent.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="mt-4 max-h-[400px] overflow-y-auto space-y-2">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 rounded-xl bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : agents.length === 0 ? (
            <div className="py-8 text-center">
              <Bot className="mx-auto h-10 w-10 text-gray-500 mb-3" />
              <p className="text-sm text-gray-400">{t('selectAgent.noAgents')}</p>
              <p className="text-xs text-gray-500 mt-1">
                {t('selectAgent.noAgentsDesc')}
              </p>
            </div>
          ) : (
            agents.map((agent) => (
              <button
                key={agent.id}
                onClick={() => {
                  onSelect(agent.id, agent.name)
                  onOpenChange(false)
                }}
                className="w-full flex items-center gap-3 rounded-xl border border-gray-700/50 bg-white/5 p-4 text-left transition-all duration-200 hover:bg-white/10 hover:border-indigo-500/30"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-500/20">
                  <Bot className="h-5 w-5 text-indigo-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-200 truncate">
                    {agent.name}
                  </p>
                  {agent.description && (
                    <p className="text-xs text-gray-400 truncate mt-0.5">
                      {agent.description}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
